import { Box, Stack } from '@mui/material';
import { Button, colors, IconButton, Typography } from '@sweep-io/sweep-design';
import { useDispatch, useSelector } from 'react-redux';
import { useIntegrations } from '../../../hooks/useIntegrations';
import {
  selectTeamsIntegrationStatus,
  setTeamsIntegrationStatus,
  TeamsConnectionStatus,
} from '../../../reducers/integrationsReducer';
import { useRunOnce } from '../../common/useRunOnce';
import { waitForWindowToClose } from '../../pages/helper';
import { randomId } from '../../../utils/teamsOAuthStateUtils';
import RestrictedTooltip from '../../common/permissions/RestrictedTooltip';
import { usePermissions } from '../../../permissions/usePermissions';
import type {
  ButtonSize,
  ButtonVariants,
} from '@sweep-io/sweep-design/dist/components/Button/types';
import usePopover from '../../../hooks/usePopover';
import { ActionsRequireMp } from '../../common/install-managed-package/getInstallManagedPackageText';
import InstallManagedPackagePopoverContent from '../../common/install-managed-package/InstallManagedPackagePopoverContent';
import IntegrationsButtonPopover from './IntegrationsButtonPopover';
import { ACTIONS_EVENTS } from '../../../services/events';
import useSendBiEvent from '../../../hooks/useSendBiEvent';
import { setTeamsStateToken } from '../../../utils/teamsOAuthStateUtils';
import { Unplug } from '@sweep-io/sweep-design/dist/icons/Unplug';
import { Info } from '@sweep-io/sweep-design/dist/icons/Info';
import { Users } from '@sweep-io/sweep-design/dist/icons/Users';
import { useSweepNotifications } from '../../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../../reducers/notificationsReducer';
import { InfoDialog } from '../../common/dialogs/InfoDialog';
import { useState } from 'react';
import { microsoftTeamsLogoUrl } from '../../common/connect-sweep-to/integrationIcons';
import { ActionsMenu } from '../../common/actions-menu/ActionsMenu';
import { useUserDialogContext } from '../user-dialog/UserDialogContext';

const SERVER_URL = import.meta.env.VITE_SERVER_URL;

const getTeamsRedirectToOAuthUrl = (state: string) =>
  `${SERVER_URL}/data-integrations/teams/redirect-to-oauth?data=${state}`;

interface TeamsConnectButtonProps {
  variant?: ButtonVariants;
  size?: ButtonSize;
  disabled?: boolean;
}

export const TeamsConnectButton = ({
  variant = 'system',
  size = 'tiny',
  disabled,
}: TeamsConnectButtonProps) => {
  const sendBiEvent = useSendBiEvent();
  const teamsStatus = useSelector(selectTeamsIntegrationStatus);
  const dispatch = useDispatch();
  const integrations = useIntegrations();
  const managedPackagePopover = usePopover();
  const infoDialogPopover = usePopover();
  const { addNotification } = useSweepNotifications();
  const [isInfoDialogForcedOpen, setIsInfoDialogForcedOpen] = useState(false);
  const { setIsTeamsInstallationOpen } = useUserDialogContext();

  const [isConnectTeamsAllowed, isDisconnectTeamsAllowed, isManageTeamsAllowed] =
    usePermissions().evaluatePermissions([
      {
        permission: [
          'create:data-integrations:teams:oauth',
          'delete:data-integrations:teams',
          'create:data-integrations:teams:install',
        ],
      },
    ]);

  useRunOnce(async () => {
    await integrations.getTeamsStatus();
    if (teamsStatus === TeamsConnectionStatus.AwaitingApproval && !isInfoDialogForcedOpen) {
      infoDialogPopover.openPopover();
      setIsInfoDialogForcedOpen(true);
    }
  });

  const connectToTeams = async () => {
    sendBiEvent({ name: ACTIONS_EVENTS.teamsConnect });
    const id = randomId(15);
    setTeamsStateToken(id);
    const _window = window.open(getTeamsRedirectToOAuthUrl(id), '_blank');

    if (_window) {
      dispatch(setTeamsIntegrationStatus(TeamsConnectionStatus.Initialization));
      await waitForWindowToClose(_window);
      const status = await integrations.getTeamsStatus();
      if (status === TeamsConnectionStatus.Connected) {
        setIsTeamsInstallationOpen(true);
        setIsInfoDialogForcedOpen(true);
      }
      if (status === TeamsConnectionStatus.Error) {
        addNotification({
          keepOpen: true,
          variant: SweepNotificationVariant.Error,
          message:
            'Connection problem. Try reconnecting, check admin approval, or wait — Teams may take up to 24 hours to update.',
        });
      }
      if (status === TeamsConnectionStatus.AwaitingApproval) {
        if (isInfoDialogForcedOpen) {
          addNotification({
            keepOpen: true,
            variant: SweepNotificationVariant.Error,
            message: `Unable to connect, please contact your administrator`,
          });
        } else {
          infoDialogPopover.openPopover();
          setIsInfoDialogForcedOpen(true);
        }
      }
    } else {
      dispatch(setTeamsIntegrationStatus(TeamsConnectionStatus.Error));
    }
  };

  const disconnectTeams = async () => {
    await integrations.disconnectTeams();
    dispatch(setTeamsIntegrationStatus(TeamsConnectionStatus.NotInitialized));
  };

  const getTeamConnectButton = (text: string) => {
    return (
      <RestrictedTooltip
        validation={{ type: 'explicit', allowed: isConnectTeamsAllowed }}
        notAllowedTitle={'To adjust integration settings, please contact your admin.'}
      >
        <Button
          variant={variant}
          disabled={!isConnectTeamsAllowed || disabled}
          onClick={() => connectToTeams()}
          size={size}
          ref={managedPackagePopover.anchorRef}
        >
          {text}
        </Button>
      </RestrictedTooltip>
    );
  };

  return (
    <>
      <IntegrationsButtonPopover
        open={managedPackagePopover.isPopoverOpen}
        anchorEl={managedPackagePopover.anchorElement}
        onClose={() => managedPackagePopover.closePopover()}
      >
        <InstallManagedPackagePopoverContent action={ActionsRequireMp.TEAMS} />
      </IntegrationsButtonPopover>

      <Stack direction="row" gap="4px">
        {teamsStatus === TeamsConnectionStatus.Connected && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color={colors.feedback.success}>
              Connected
            </Typography>
            <ActionsMenu
              iconSize="tiny"
              actions={[
                {
                  label: 'Manage Teams',
                  value: 'Manage',
                  actionIcon: <Users />,
                  disabled: !isManageTeamsAllowed,
                  disabledTooltip: 'To adjust integration settings, please contact your admin.',
                },
                {
                  label: 'Disconnect',
                  value: 'Disconnect',
                  actionIcon: <Unplug color={colors.blush[600]} />,
                  disabled: !isDisconnectTeamsAllowed,
                  disabledTooltip: 'To adjust integration settings, please contact your admin.',
                  actionSx: { color: colors.blush[600] },
                },
              ]}
              onClick={(action) => {
                if (action?.value === 'Disconnect') {
                  disconnectTeams();
                }
                if (action?.value === 'Manage') {
                  setIsTeamsInstallationOpen(true);
                }
              }}
              iconVariant="system"
              menuWidth="200px"
            />
          </Box>
        )}

        {teamsStatus === TeamsConnectionStatus.NotInitialized && getTeamConnectButton('Connect')}

        {teamsStatus === TeamsConnectionStatus.Initialization && (
          <Button variant={variant} size={size} loading={true}></Button>
        )}

        {teamsStatus === TeamsConnectionStatus.AwaitingApproval && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <IconButton onClick={infoDialogPopover.openPopover} size="tiny" variant="flat">
              <Info size="medium" />
            </IconButton>
            {getTeamConnectButton('Complete')}
          </Box>
        )}

        {(teamsStatus === TeamsConnectionStatus.Error ||
          teamsStatus === TeamsConnectionStatus.InvalidCredentials) &&
          getTeamConnectButton('Reconnect')}
      </Stack>

      <InfoDialog
        handleClose={infoDialogPopover.closePopover}
        open={infoDialogPopover.isPopoverOpen}
        titleJsx={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src={microsoftTeamsLogoUrl} width={20} height={20} alt="Microsoft Teams" />
            <Typography variant="body-medium" color={colors.grey[700]}>
              Microsoft office Teams
            </Typography>
          </Box>
        }
        PaperPropsSx={{ width: '470px' }}
        dialogActions={
          <Button onClick={infoDialogPopover.closePopover} size="small">
            Got it
          </Button>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 3 }}>
          <Typography variant="h4">Pending Admin approval</Typography>
          <Typography variant="body" color={colors.grey[700]}>
            Teams require admin approval to set up this integration. Once approved, click "Complete"
            to finalize the process
          </Typography>
        </Box>
      </InfoDialog>
    </>
  );
};
